import { Button, Drawer, Input, InputNumber, Message, Modal, Select } from "@arco-design/web-react";
import { Upload, X } from "lucide-react";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import { MEDIA_FILE_MAX_BYTES, MEDIA_IMAGE_MAX_BYTES } from "../../media/config";
import { uploadMedia } from "../../media/repo";
import { resourcePointMediaSceneOptions } from "../config";
import {
  createResourcePointMedia,
  fetchResourcePointMedia,
  obsoleteResourcePointMedia,
  updateResourcePointMedia,
} from "../repo";
import {
  emptyHeritageDescription,
  parseHeritageDescription,
  resourcePointMutationErrorMessage,
  serializeHeritageDescription,
  validateMediaMutation,
  type ResourcePointMediaMutationErrors,
} from "../service";
import type {
  ResourcePointHeritageDescription,
  ResourcePointMediaItem,
  ResourcePointMediaMutation,
  ResourcePointMediaScene,
  ResourcePointSpuItem,
} from "../types";
import { ResourcePointFormRow } from "./ResourcePointFormRow";
import { ResourcePointHeritageDescriptionFields } from "./ResourcePointHeritageDescriptionFields";
import { ResourcePointHeritageMarkdownField } from "./ResourcePointHeritageMarkdownField";

export type ResourcePointHeritageUploadState =
  | {
    type: "create";
    spu: ResourcePointSpuItem;
    lockedScene?: ResourcePointMediaScene;
  }
  | { type: "edit"; spu: ResourcePointSpuItem; item: ResourcePointMediaItem };

type ResourcePointHeritageUploadDrawerProps = {
  state: ResourcePointHeritageUploadState | null;
  onClose: () => void;
  onSaved: () => void;
};

const uniqueScenes = new Set<ResourcePointMediaScene>(["introduction", "ppt", "description"]);

const emptyMutation = (mediaScene: ResourcePointMediaScene): ResourcePointMediaMutation => ({
  mediaScene,
  mediaName: mediaScene === "introduction"
    ? "传承人"
    : mediaScene === "ppt"
      ? "PPT"
      : mediaScene === "description"
        ? "描述"
        : "",
  fileUrl: "",
  fileExt: undefined,
  contentText: undefined,
  sortNo: 0,
  remark: "",
});

/** 获取素材场景的中文名称。 */
function sceneLabel(scene: ResourcePointMediaScene): string {
  return resourcePointMediaSceneOptions.find((item) => item.value === scene)?.label ?? scene;
}

/** 判断当前场景是否只接受图片素材。 */
function isImageScene(mediaScene: ResourcePointMediaScene): boolean {
  return mediaScene === "teaching" || mediaScene === "product";
}

/** 获取当前素材场景允许选择的文件类型。 */
function fileAcceptForScene(mediaScene: ResourcePointMediaScene): string {
  if (isImageScene(mediaScene)) return "image/*";
  if (mediaScene === "ppt") return ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
  return "image/*,video/*,.pdf,.ppt,.pptx,.doc,.docx";
}

/** 确认是否替换只允许单条内容的现有素材。 */
function confirmReplaceUnique(scene: ResourcePointMediaScene): Promise<boolean> {
  const label = sceneLabel(scene);
  return new Promise((resolve) => {
    Modal.confirm({
      title: `替换${label}`,
      content: `该考察点已有${label}。确认后将作废旧内容，并保存当前新内容。`,
      okText: "确认替换",
      cancelText: "取消",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/** 解析新建抽屉默认选中的素材场景。 */
function defaultCreateScene(state: Extract<ResourcePointHeritageUploadState, { type: "create" }>): ResourcePointMediaScene {
  return state.lockedScene ?? "teaching";
}

/** 判断文件是否为支持的 PowerPoint 格式。 */
function isPptFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith(".ppt") || name.endsWith(".pptx");
}

/** 从文件名中提取小写扩展名。 */
function extensionFromName(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed.includes(".")) return undefined;
  return trimmed.split(".").pop()?.toLowerCase() || undefined;
}

/** 从不含查询参数的文件地址中提取扩展名。 */
function extensionFromFileUrl(fileUrl: string): string | undefined {
  const path = fileUrl.split("?")[0] ?? fileUrl;
  const name = path.split("/").pop() ?? "";
  return extensionFromName(decodeURIComponent(name));
}

/** 非遗素材上传：通用 /media/upload 后落库 resource_point_media。 */
export function ResourcePointHeritageUploadDrawer({
  state,
  onClose,
  onSaved,
}: ResourcePointHeritageUploadDrawerProps) {
  const [payload, setPayload] = useState<ResourcePointMediaMutation>(emptyMutation("teaching"));
  const [description, setDescription] = useState<ResourcePointHeritageDescription>(
    emptyHeritageDescription,
  );
  const [errors, setErrors] = useState<ResourcePointMediaMutationErrors>({});
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const imageOnly = isImageScene(payload.mediaScene);
  const isIntroduction = payload.mediaScene === "introduction";
  const isDescription = payload.mediaScene === "description";
  const isPpt = payload.mediaScene === "ppt";
  const hideMetaFields = isIntroduction || isDescription || isPpt;
  const hideMediaName = isIntroduction || isPpt || isDescription;
  const hideSceneSelect = state?.type === "edit" || Boolean(state?.type === "create" && state.lockedScene);
  const sceneOptions = useMemo(() => {
    if (state?.type === "create" && state.lockedScene) {
      return resourcePointMediaSceneOptions.filter((item) => item.value === state.lockedScene);
    }
    return [...resourcePointMediaSceneOptions];
  }, [state]);

  useEffect(() => {
    if (!state) return;
    setErrors({});
    setErrorMessage("");
    if (state.type === "create") {
      setPayload(emptyMutation(defaultCreateScene(state)));
      setDescription({ ...emptyHeritageDescription });
      return;
    }
    setPayload({
      mediaScene: state.item.mediaScene as ResourcePointMediaScene,
      mediaName: state.item.mediaName,
      fileUrl: state.item.fileUrl,
      fileExt: state.item.fileExt,
      contentText: state.item.contentText,
      sortNo: state.item.sortNo,
      remark: state.item.remark,
    });
    setDescription(parseHeritageDescription(state.item.contentText));
  }, [state]);

  /** 校验并上传当前选择的素材文件。 */
  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imageOnly && !file.type.startsWith("image/")) {
      setErrorMessage("当前素材仅支持上传图片");
      Message.error("当前素材仅支持上传图片");
      event.target.value = "";
      return;
    }
    if (isPpt && !isPptFile(file)) {
      setErrorMessage("PPT 仅支持 .ppt 或 .pptx 文件");
      Message.error("PPT 仅支持 .ppt 或 .pptx 文件");
      event.target.value = "";
      return;
    }
    const maxBytes = imageOnly ? MEDIA_IMAGE_MAX_BYTES : MEDIA_FILE_MAX_BYTES;
    if (file.size > maxBytes) {
      const limitMb = Math.round(maxBytes / (1024 * 1024));
      const message = `文件过大，请压缩到 ${limitMb} MB 以内后重试`;
      setErrorMessage(message);
      Message.error(message);
      event.target.value = "";
      return;
    }
    setUploading(true);
    setErrorMessage("");
    try {
      const kind = imageOnly || file.type.startsWith("image/") ? "image" : "file";
      const result = await uploadMedia(file, kind);
      const fileExt = extensionFromName(file.name) ?? extensionFromFileUrl(result.fileUrl);
      setPayload((current) => ({
        ...current,
        fileUrl: result.fileUrl,
        mediaName: current.mediaName || (isPpt ? "PPT" : file.name),
        fileExt,
      }));
    } catch (error) {
      const message = resourcePointMutationErrorMessage(error);
      setErrorMessage(message);
      Message.error(message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  /** 保存新增或编辑后的素材记录。 */
  async function handleSave() {
    if (!state || pending || uploading) return;
    const inferredExt = payload.fileExt
      ?? extensionFromFileUrl(payload.fileUrl)
      ?? undefined;
    const nextPayload: ResourcePointMediaMutation = {
      ...payload,
      contentText: payload.mediaScene === "description"
        ? serializeHeritageDescription(description)
        : payload.mediaScene === "introduction"
          ? (payload.contentText ?? "").trim()
          : payload.contentText,
      fileUrl: (payload.mediaScene === "description" || payload.mediaScene === "introduction")
        ? ""
        : payload.fileUrl.trim(),
      fileExt: inferredExt,
      mediaName: payload.mediaName.trim()
        || (payload.mediaScene === "introduction"
          ? "传承人"
          : payload.mediaScene === "ppt"
            ? "PPT"
            : payload.mediaScene === "description"
              ? "描述"
              : payload.mediaName),
    };
    const nextErrors = validateMediaMutation(nextPayload);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      Message.error("请先完善必填信息后再保存");
      return;
    }
    setErrors({});
    setErrorMessage("");

    const spuId = state.spu.resourcePointSpuId;
    let replaced = false;

    if (state.type === "create" && uniqueScenes.has(nextPayload.mediaScene as ResourcePointMediaScene)) {
      let existing: ResourcePointMediaItem | undefined;
      try {
        const mediaItems = await fetchResourcePointMedia(spuId);
        existing = mediaItems.find((item) => item.mediaScene === nextPayload.mediaScene);
      } catch (error) {
        const message = resourcePointMutationErrorMessage(error);
        setErrorMessage(message);
        Message.error(message);
        return;
      }
      if (existing) {
        const confirmed = await confirmReplaceUnique(nextPayload.mediaScene as ResourcePointMediaScene);
        if (!confirmed) return;
        setPending(true);
        try {
          await obsoleteResourcePointMedia(spuId, existing.resourcePointMediaId);
          replaced = true;
        } catch (error) {
          const message = resourcePointMutationErrorMessage(error);
          setErrorMessage(message);
          Message.error(message);
          setPending(false);
          return;
        }
      }
    }

    setPending(true);
    try {
      if (state.type === "create") {
        await createResourcePointMedia(spuId, nextPayload);
        Message.success(replaced ? `已替换${sceneLabel(nextPayload.mediaScene as ResourcePointMediaScene)}` : "素材保存成功");
      } else {
        await updateResourcePointMedia(
          spuId,
          state.item.resourcePointMediaId,
          nextPayload,
        );
        Message.success("素材更新成功");
      }
      onSaved();
      onClose();
    } catch (error) {
      const message = resourcePointMutationErrorMessage(error);
      setErrorMessage(message);
      Message.error(message);
    } finally {
      setPending(false);
    }
  }

  const title = state?.type === "edit"
    ? state.item.mediaScene === "introduction"
      ? "编辑传承人"
      : state.item.mediaScene === "description"
        ? "编辑描述"
        : state.item.mediaScene === "ppt"
          ? "替换 PPT"
          : "编辑非遗素材"
    : state?.lockedScene === "introduction"
      ? "填写传承人"
      : state?.lockedScene
        ? `上传${sceneLabel(state.lockedScene)}`
        : "上传非遗素材";

  return (
    <Drawer
      className="resource-point-heritage-drawer"
      title={title}
      visible={Boolean(state)}
      width={isIntroduction || isDescription ? 560 : 480}
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={(
        <Button type="primary" long loading={pending || uploading} onClick={handleSave}>
          保存
        </Button>
      )}
      onCancel={() => {
        if (pending || uploading) return;
        onClose();
      }}
    >
      <div className={`resource-point-drawer__body resource-point-drawer__form${
        isIntroduction || isDescription ? " resource-point-drawer__form--stacked" : ""
      }`}>
        {errorMessage ? <div className="resource-point-error" role="alert">{errorMessage}</div> : null}
        {hideSceneSelect ? null : (
          <ResourcePointFormRow label="素材类型" required error={errors.mediaScene}>
            <Select
              aria-label="素材类型"
              options={sceneOptions}
              value={payload.mediaScene}
              onChange={(mediaScene: ResourcePointMediaScene) => {
                setPayload({
                  ...emptyMutation(mediaScene),
                });
                setErrors({});
              }}
            />
          </ResourcePointFormRow>
        )}
        {isIntroduction ? (
          <ResourcePointHeritageMarkdownField
            value={payload.contentText ?? ""}
            error={errors.contentText}
            onChange={(contentText) => {
              setPayload({ ...payload, contentText });
              setErrors((current) => ({ ...current, contentText: undefined }));
            }}
          />
        ) : null}
        {isDescription ? (
          <ResourcePointHeritageDescriptionFields
            value={description}
            error={errors.contentText}
            onChange={(next) => {
              setDescription(next);
              setErrors((current) => ({ ...current, contentText: undefined }));
            }}
          />
        ) : null}
        {!isIntroduction && !isDescription ? (
          <ResourcePointFormRow
            label={imageOnly ? "上传图片" : isPpt ? "上传 PPT" : "上传文件"}
            required
            error={errors.fileUrl}
          >
            <label className="resource-point-heritage-file-button">
              <Upload size={16} />
              {uploading ? "上传中..." : imageOnly ? "选择图片" : isPpt ? "选择 PPT 文件" : "选择并上传"}
              <input
                aria-label={imageOnly ? "上传图片" : isPpt ? "上传 PPT" : "上传文件"}
                type="file"
                accept={fileAcceptForScene(payload.mediaScene)}
                disabled={uploading || pending}
                onChange={handleUpload}
              />
            </label>
          </ResourcePointFormRow>
        ) : null}
        {!isIntroduction && !isDescription && payload.fileUrl ? (
          <div className="resource-point-heritage-file-url">{payload.fileUrl}</div>
        ) : null}
        {!hideMediaName ? (
          <ResourcePointFormRow label="素材名称" required error={errors.mediaName}>
            <Input
              aria-label="素材名称"
              value={payload.mediaName}
              onChange={(mediaName) => setPayload({ ...payload, mediaName })}
            />
          </ResourcePointFormRow>
        ) : null}
        {!hideMetaFields ? (
          <ResourcePointFormRow label="排序号" error={errors.sortNo}>
            <InputNumber
              aria-label="排序号"
              min={0}
              precision={0}
              value={payload.sortNo}
              onChange={(sortNo) => setPayload({
                ...payload,
                sortNo: typeof sortNo === "number" && Number.isFinite(sortNo) ? sortNo : 0,
              })}
            />
          </ResourcePointFormRow>
        ) : null}
        {!hideMetaFields ? (
          <ResourcePointFormRow label="备注" error={errors.remark}>
            <Input.TextArea
              aria-label="素材备注"
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={payload.remark}
              onChange={(remark) => setPayload({ ...payload, remark })}
            />
          </ResourcePointFormRow>
        ) : null}
      </div>
    </Drawer>
  );
}
